/**
 * PKCE (Proof Key for Code Exchange) helpers.
 *
 * Generates a random verifier, computes SHA-256 challenge,
 * and stores/retrieves the verifier from sessionStorage.
 */

const STORAGE_KEY = 'pkce_code_verifier';

const base64UrlEncode = (bytes: Uint8Array): string =>
  btoa(Array.from(bytes, b => String.fromCharCode(b)).join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/** Generate a cryptographically random code verifier. */
const generateVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

/** Compute SHA-256 code challenge from verifier. */
const computeChallenge = async (verifier: string): Promise<string> => {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

/** Create and store a new PKCE pair. Returns the challenge to send to Cognito. */
export const createPkceChallenge = async (): Promise<string> => {
  const verifier = generateVerifier();
  sessionStorage.setItem(STORAGE_KEY, verifier);
  return computeChallenge(verifier);
};

/** Retrieve and consume the stored verifier (one-time use). */
export const consumePkceVerifier = (): string | null => {
  const verifier = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  return verifier;
};
